import { FormikProps } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";

export interface NoteFormSchema {
    value: string;
}

/**
 * Shared hook for inline note-editing logic used by NoteCell and CloseNoteCell.
 *
 * `closePopover` — caller-provided fn to close the popover (Radix/base-ui
 * controlled state).
 */
export const useNoteEditing = (
    note: string | null | undefined,
    onUpdate: (payload: string) => void,
    closePopover: () => void,
) => {
    const [isOpened, setIsOpened] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const formRef = useRef<FormikProps<NoteFormSchema>>(null);

    const handleUpdate = useCallback(
        (value: string) => {
            const trimmedValue = value.trim();
            if (trimmedValue !== (note ?? "")) {
                onUpdate(trimmedValue);
            }
        },
        [note, onUpdate],
    );

    const handleSubmit = useCallback(
        ({ value }: NoteFormSchema) => {
            handleUpdate(value);
            closePopover();
        },
        [handleUpdate, closePopover],
    );

    const handleBlur = useCallback(() => {
        const currentValue = formRef.current?.values?.value;
        if (currentValue !== undefined) {
            handleUpdate(currentValue);
        }
        closePopover();
    }, [handleUpdate, closePopover]);

    const handleFocus = useCallback(() => {
        if (textareaRef.current) {
            textareaRef.current.select();
        }
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === "Enter") {
                if (event.shiftKey) {
                    return;
                }
                event.preventDefault();
                formRef.current?.submitForm();
            }
            if (event.key === "Escape") {
                event.preventDefault();
                formRef.current?.resetForm();
                closePopover();
            }
        },
        [closePopover],
    );

    useEffect(() => {
        if (isOpened && textareaRef.current) {
            const timerId = setTimeout(() => {
                textareaRef.current?.focus();
                textareaRef.current?.select();
            }, 10);
            return () => clearTimeout(timerId);
        }
        return undefined;
    }, [isOpened]);

    return {
        isOpened,
        setIsOpened,
        textareaRef,
        formRef,
        handleSubmit,
        handleBlur,
        handleFocus,
        handleKeyDown,
    };
};
